import axios, { AxiosError } from "axios";
import { useCallback, useContext } from "react";
import useSWR from "swr";
import { AuthContext } from "../../../context/AuthContext";
import { AdminEndpointEnum } from "../../../enums/endpoints";
import { ADMIN_API_URL } from "../../../utils/env";
import { ToastEnum, ToastStatusEnum, useToast } from "../../toast";

export interface INerfedMMVolumeWeightParams {
  zero_probability: number;
  max_weight: number;
}

// useNerfedMMVolumeWeightParams wraps GET/POST /nerfed-mm-volume-weight-params.
// Backend enforces zero_probability ∈ [0, 1] and max_weight ∈ (0, 1] and
// returns 400 with a message on violation; we surface that verbatim.
// 30s poll keeps the UI in sync with edits from other admins.
export const useNerfedMMVolumeWeightParams = () => {
  const { queryConfig, tokenId } = useContext(AuthContext);
  const { addToast } = useToast();

  const endpoint = ADMIN_API_URL(
    AdminEndpointEnum.NERFED_MM_VOLUME_WEIGHT_PARAMS
  );
  const swr = useSWR<INerfedMMVolumeWeightParams, AxiosError>(
    tokenId ? [endpoint, tokenId] : undefined,
    async () => {
      const response = await axios.get<Partial<INerfedMMVolumeWeightParams>>(
        endpoint,
        queryConfig()
      );
      const raw = response.data || {};
      return {
        zero_probability: raw.zero_probability ?? 0,
        max_weight: raw.max_weight ?? 0,
      };
    },
    { refreshInterval: 30000 }
  );

  const withAuthHeaders = useCallback(
    () => ({
      headers: {
        Authorization: `Bearer ${tokenId}`,
      },
    }),
    [tokenId]
  );

  const updateNerfedMMVolumeWeightParams = useCallback(
    async (params: INerfedMMVolumeWeightParams) => {
      try {
        await axios.post(endpoint, params, withAuthHeaders());
        const ev =
          (1 - params.zero_probability) * (params.max_weight / 2);
        addToast(
          {
            type: ToastEnum.SIMPLE,
            header: "Nerfed MM weight params updated",
            subheader: `zero_probability: ${params.zero_probability} | max_weight: ${params.max_weight} | EV: ${ev.toFixed(4)}`,
            status: ToastStatusEnum.SUCCESS,
          },
          4000
        );
        swr.mutate();
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(error);
        const payload = (error as AxiosError)?.response?.data as any;
        const subheader = payload?.error || payload?.message || "Unknown error";
        addToast(
          {
            type: ToastEnum.SIMPLE,
            header: "Update failed",
            subheader,
            status: ToastStatusEnum.ERROR,
          },
          5000
        );
      }
    },
    [addToast, endpoint, swr, withAuthHeaders]
  );

  return {
    ...swr,
    updateNerfedMMVolumeWeightParams,
  };
};
